
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface ExerciseQueryOptions {
  routineId: string;
  enabled?: boolean;
  staleTime?: number;
}

export const useUnifiedExerciseQuery = ({ 
  routineId, 
  enabled = true,
  staleTime = 60000 // Increased default stale time for zero-refetch strategy
}: ExerciseQueryOptions) => {
  return useQuery({
    queryKey: ["unified-exercises", routineId],
    queryFn: async () => {
      console.log(`🔍 Zero-refetch unified query for routine ${routineId}`);
      
      const { data, error } = await supabase
        .from("exercises")
        .select(`
          id,
          name,
          sets,
          reps,
          weight,
          position,
          exercise_rest_time,
          set_rest_time,
          set_data,
          video_url
        `)
        .eq("routine_id", routineId)
        .order("position", { ascending: true });

      if (error) {
        console.error("❌ Zero-refetch query error:", error);
        throw error;
      }

      const processedData = data?.map(exercise => ({
        ...exercise,
        set_data: exercise.set_data?.map((set: any) => ({
          reps: Number(set.reps) || 0,
          weight: Number(set.weight) || 0
        })) || [],
        exercise_rest_time: exercise.exercise_rest_time !== null ? Number(exercise.exercise_rest_time) : null,
        set_rest_time: exercise.set_rest_time !== null ? Number(exercise.set_rest_time) : null
      })) || [];

      console.log(`✅ Zero-refetch query returned ${processedData.length} exercises for routine ${routineId}`);
      return processedData;
    },
    enabled: !!routineId && enabled,
    staleTime, // Use the optimized stale time
    gcTime: 600000, // Longer garbage collection time
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false, // Disable auto-refetch on reconnect
    retry: 1, // Reduce retries to minimize refetches
    retryDelay: 2000, // Longer retry delay
    notifyOnChangeProps: ['data', 'error'], // Only notify on actual data changes
  });
};
